import {Config, Modules, Source} from '@buncha/utils/types';
import {Locale, LocalizationFileData, Module} from './types';
import {getConfig, nothing, showDevTools} from '@buncha/utils/appInfo';
import {useAppDispatch, useAppSelector} from '@buncha/config/redux/hooks';
import {useCallback, useRef, useState} from 'react';

import {Analytics} from '@buncha/config/analytics/analytics';
import {Constants} from '@buncha/onBoarding/types';
import {StorageKeys} from '@buncha/config/storage/type';
import {isWeb} from '@buncha/utils/common';
import {localizationAction} from './reducer';
import {metaInfoConstants} from '@buncha/config/logger/utils';
import {storage} from '@buncha/config/storage';
import {useGetConstantsForApp} from '@buncha/common/hooks/common';

export function useFetchLocalizationData() {
  const appDispatch = useAppDispatch();
  const fetchUrlApp = showDevTools()
    ? nothing(getConfig(Config.awsLocalizationUrl))
    : nothing(getConfig(Config.awsLocalizationUrlRelease));
  const fetchUrlWeb = showDevTools()
    ? getConfig(Config.awsLocalizationUrlWeb)
    : getConfig(Config.awsLocalizationUrlReleaseWeb);
  const fetchUrl = isWeb() ? fetchUrlWeb : fetchUrlApp;
  const env = getConfig(Config.devTool);
  const language = 'en-us'; // only one language for now
  const retry = useRef(true);
  const retryCount = useRef(0);
  const totalRetryCount = useRef(0);
  const {getConstantsForApp} = useGetConstantsForApp();
  const [isCompleted, setIsCompleted] = useState(false);

  const successCallback = useCallback(
    (response: {statusCode: number; body: string}) => {
      const data: LocalizationFileData = JSON.parse(response?.body);
      const moduleData: Module = data?.modules;
      if (appDispatch)
        appDispatch(localizationAction.setModuleData(moduleData));
      storage.setItem(StorageKeys.localizationModuleData, response?.body);
      storage.setItem(StorageKeys.appVersion, metaInfoConstants.appVersion);
    },
    [appDispatch],
  );

  const fetchRetryCount = useCallback(async () => {
    retry.current = false;
    const constants: Constants = await getConstantsForApp();
    const retryConfig = constants?.RETRY_CONFIG
      ? JSON.parse(constants.RETRY_CONFIG)
      : {};
    const maxApiCount = retryConfig?.maxApiCount ?? 0;
    totalRetryCount.current = maxApiCount;
    return maxApiCount;
  }, [getConstantsForApp]);

  const apiRetryAnalytics = useCallback(
    (
      retryCounter: number,
      environment: string,
      versionNo: string | null,
      err: any,
    ) => {
      const count = totalRetryCount.current - retryCounter;
      const payload = {
        env: environment,
        version: versionNo,
        source: Source.fulfillment,
      };
      const params = {
        apiSource: Source.fulfillment,
        apiName: 'awsLocalization',
        apiPayload: JSON.stringify(payload),
        error: err?.name,
        type: 'Error',
        retryCount: count,
        errorInfo: String(err),
      };
      Analytics.eventWithProps(
        `api_retry_error_${count}time_api_called`,
        params,
      );
    },
    [],
  );

  const fetchFromLocalFile = useCallback(
    (storedVersion: string | null, storedModuleData: string | null) => {
      const fileData = storedModuleData?.length
        ? JSON.parse(storedModuleData ?? '{}')
        : require('./localData.json');
      const localModuleData = fileData?.modules;
      if (appDispatch) {
        appDispatch(localizationAction.setModuleData(localModuleData));
        if (storedVersion)
          appDispatch(localizationAction.setVersion(storedVersion));
      }
      storage.setItem(
        StorageKeys.localizationModuleData,
        JSON.stringify(fileData),
      );
    },
    [appDispatch],
  );

  const matchVersion = useCallback(
    async (storedVersion: string | null, storedModuleData: string | null) => {
      const constants: Constants = await getConstantsForApp();
      let versionObj;
      try {
        versionObj = JSON.parse(constants?.FULFILLMENT_LOCALIZATION_VERSION);
      } catch {}

      const localizationVersion = String(
        showDevTools() ? versionObj?.debug : versionObj?.release,
      );

      if (!localizationVersion?.length)
        return fetchFromLocalFile(storedVersion, storedModuleData);

      const isNotMatching = localizationVersion !== storedVersion;
      if (localizationVersion && appDispatch) {
        appDispatch(localizationAction.setVersion(localizationVersion));
        storage.setItem(
          StorageKeys.localizationModuleVersion,
          localizationVersion,
        );
      }
      if (!storedVersion?.length) return true;

      return isNotMatching;
    },
    [appDispatch, fetchFromLocalFile, getConstantsForApp],
  );

  const getStoredData = useCallback(async () => {
    const storedVersion = await storage.getItem(
      StorageKeys.localizationModuleVersion,
    );
    const storedModuleData = await storage.getItem(
      StorageKeys.localizationModuleData,
    );
    const storedAppVersion = await storage.getItem(StorageKeys.appVersion);
    return {storedVersion, storedModuleData, storedAppVersion};
  }, []);

  const fetchLocalizationData = useCallback(async () => {
    const {storedVersion, storedModuleData, storedAppVersion} =
      await getStoredData();
    const fetchFromLambda = await matchVersion(storedVersion, storedModuleData);
    if (
      (!fetchFromLambda || !fetchUrl) &&
      metaInfoConstants.appVersion === storedAppVersion
    ) {
      fetchFromLocalFile(storedVersion, storedModuleData);
      setIsCompleted(true);
      return;
    }
    return fetch(
      `${fetchUrl}?env=${env}&language=${language}&source=${Source.fulfillment}`,
    )
      .then(async res => {
        const response = await res.json();
        if (res.ok) successCallback(response);
        else throw new Error(response?.message);
      })
      .catch(async (err: any) => {
        if (retry.current) {
          const maxApiCount = await fetchRetryCount();
          retryCount.current = maxApiCount;
        }
        if (retryCount.current >= 1) {
          let count = retryCount.current;
          count = retryCount.current -= 1;
          apiRetryAnalytics(count, env, storedVersion, err);
          await fetchLocalizationData();
        } else {
          fetchFromLocalFile(storedVersion, storedModuleData);
        }
      })
      .finally(() => {
        setIsCompleted(true);
      });
  }, [
    getStoredData,
    apiRetryAnalytics,
    env,
    fetchFromLocalFile,
    fetchRetryCount,
    fetchUrl,
    successCallback,
    matchVersion,
  ]);

  return {fetchLocalizationData, isCompleted};
}

export function useGetLocale(
  module: Modules,
  screen: string,
): Locale | undefined {
  return useAppSelector(
    gState => gState?.localization?.moduleData?.[module]?.[screen],
  );
}
